/**
 * VoiceNoteButton — Mikrofon → Whisper-Transkription → Textfeld-Anhang.
 * ====================================================================
 * Iter89z-wiz3 (2026-02-26).
 *
 * MVP: Button öffnet Aufnahme, nächster Klick stoppt und sendet die
 * webm-Datei an das Backend (Whisper deutsch). Backend hängt das
 * Ergebnis direkt an das Zielfeld an (append=true), danach wird das
 * Formular neu geladen.
 */
import { useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";


export default function VoiceNoteButton({ projectId, fieldKey, onApplied }) {
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);

  const start = async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error("Mikrofon nicht verfügbar in diesem Browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: mime });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        if (blob.size < 1000) {
          toast.info("Aufnahme zu kurz");
          return;
        }
        setSending(true);
        try {
          const form = new FormData();
          form.append("file", blob, `voice_${fieldKey}.webm`);
          form.append("field_key", fieldKey);
          form.append("append", "true");
          const r = await api.post(
            `/projects/${projectId}/vcheck-wp/transcribe`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          if (r.data?.text) {
            toast.success(`Transkription übernommen (${r.data.text.length} Zeichen)`);
            onApplied?.();
          } else {
            toast.info("Keine Sprache erkannt");
          }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Transkription fehlgeschlagen");
        } finally {
          setSending(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Mikrofon-Zugriff verweigert");
    }
  };

  const stop = () => recorderRef.current?.stop();

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={sending}
      data-testid={`voice-note-${fieldKey}`}
      className={`h-9 px-3 rounded-lg inline-flex items-center gap-1.5 text-[12px] font-semibold transition ${
        recording
          ? "bg-red-500 hover:bg-red-400 text-white animate-pulse"
          : sending
            ? "bg-zinc-800 text-zinc-400 opacity-70"
            : "bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 text-blue-200"
      }`}
      title={recording ? "Aufnahme stoppen" : "Sprachnotiz aufnehmen"}
    >
      {sending ? <Loader2 size={12} className="animate-spin" />
       : recording ? <Square size={12} fill="currentColor" />
       : <Mic size={12} />}
      {sending ? "Transkribiere …" : recording ? "Stop" : "Sprach­notiz"}
    </button>
  );
}
